// Shared edit-permission rule: room owners can always edit; anyone can edit
// their own resource; admins can edit anyone's resource except an owner's or
// another admin's. Mirrors the per-component checks already used in
// FilesTab.tsx / SnippetContainer.tsx (canEditRes / canEditSnippet /
// canManageFolderPermission) so new call sites don't reinvent the rule.
export type CanEditResourceParams = {
    creatorId: string;
    clientId: string | null;
    memberRole?: string;
    roomCreatorClientId?: string;
    membersList: { clientId: string; role: string }[];
};

export const canEditResource = ({
    creatorId,
    clientId,
    memberRole,
    roomCreatorClientId,
    membersList,
}: CanEditResourceParams): boolean => {
    if (!clientId) return false;

    const isRoomOwner = memberRole === 'owner' || roomCreatorClientId === clientId;
    if (isRoomOwner) return true;
    if (creatorId === clientId) return true;

    if (memberRole === 'admin') {
        const creatorMember = membersList.find(m => m.clientId === creatorId);
        if (creatorMember?.role !== 'admin' && creatorMember?.role !== 'owner') return true;
    }

    return false;
};

// Can the current member add/upload resources at all (room-wide gate). Mirrors
// the inline canAdd() previously duplicated in FileExplorerTab.tsx.
export const canAddResource = (
    memberDetails: { canCreate: boolean; role: string } | null
): boolean => {
    if (!memberDetails) return false;
    return (
        memberDetails.canCreate === true ||
        memberDetails.role === 'admin' ||
        memberDetails.role === 'owner'
    );
};

export type CanCreateInFolderParams = {
    clientId: string | null;
    yourRole: string | null;
    canAdd: boolean;
};

// "Can I create a brand new item in this folder" — same role/creator/open-permission
// rule canDropIntoFolder (driveDnd.ts) uses for dragging an existing item into a
// folder, minus the dragged-item's own permission check. Mirrors the inline
// canCreateInFolder previously duplicated in FileExplorerTab.tsx.
export const canCreateInFolder = (
    folder: { creatorId: string; folderPermission?: string | null },
    params: CanCreateInFolderParams
): boolean => {
    if (!params.canAdd) return false;
    if (params.yourRole === 'owner' || params.yourRole === 'admin') return true;
    if (folder.creatorId === params.clientId) return true;
    return (folder.folderPermission ?? 'restricted') === 'open';
};
